import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { MapPin, Buildings, Handshake } from '@phosphor-icons/react';
import { BreadcrumbNavigation } from '../../../components/BreadcrumbNavigation';
import { Link } from '../../../components/Link';
import { CompanyLogo } from '../../../components/CompanyLogo';
import { JcCard } from '../../../components/jc/JcCard';
import { EmployerScoreGauge } from '../../../components/jc/EmployerScoreGauge';
import { mapEmployerScoreToTier } from '../../../utils/employer-score/mapScoreToTier';
import { TrendIndicator, type TrendDirection } from '../../../components/jc/TrendIndicator';
import { isNetworkWorthy } from '../../../utils/best-companies/is-network-worthy';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { salaryHubUrlPath } from '../../../utils/routing/salary-url-path';
import type { AlgoliaCompanyRecord } from '../../../types/company-search-record';
import type { Data, ExamplePosting } from './+data.server';
import type { SalaryStatistics } from '../../../lambda/types';
import type { SnapshotTrend } from '../../../lambda/utils/marketSnapshotQuery';
import { SALARY_ROLE_PAGE_SCRIPT_ID, getSalaryRoleDataFromScript } from './parseSalaryRoleInitialScript';
import { ChartPopoverPortal } from '../../job/@jobId/@seoSlug/components/ChartPopoverPortal';
import { usePrefersFineHover } from '../../job/@jobId/@seoSlug/utils/usePrefersFineHover';
import { JD_MORE_INFO_CHART_HIT } from '../../../utils/job-detail/job-detail-more-info-affordance';
import { getFinnishRoleGenitive } from '../../../utils/i18n/finnish-role-genitive';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';
import { RoleFollowButton } from '../../../components/roles/RoleFollowButton';
import { TaxIcebergLink } from '../../../components/tax-iceberg/TaxIcebergLink';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildHubIndexLinks, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { findRoleByDisplayName, findRoleForAnyLocaleTitle } from '../../../utils/role/find-role-for-title';
import { buildRoleHubPath } from '../../../utils/role/role-hub-config';
import { finnishRolePhrase } from '../../../utils/seo/generate-srp-seo-meta';

const messages = defineMessages({
  breadcrumbSalaryData: {
    id: 'salaryRolePage.breadcrumb.salaryData',
    defaultMessage: 'Salary Data',
    description: 'Breadcrumb link back to the salary hub from a role salary page',
  },
  heading: {
    id: 'salaryRolePage.heading',
    defaultMessage: '{roleTitle} Salary in Finland',
    description: 'Main heading of a role salary page, where {roleTitle} is the job title',
  },
  intro: {
    id: 'salaryRolePage.intro',
    defaultMessage: 'Based on job listing data from Finnish employers. Updated regularly.',
    description: 'Introductory sentence on a role salary page explaining the data source',
  },
  alsoKnownAs: {
    id: 'salaryRolePage.alsoKnownAs',
    defaultMessage: 'Also listed under: {titles}',
    description:
      'Shown when this role is also posted under other job titles that this page covers too, so a visitor who searched one of those titles can see they landed on the right page. {titles} is a comma-separated list of English job titles.',
  },
  seeOpenJobs: {
    id: 'salaryRolePage.seeOpenJobs',
    defaultMessage: 'See open {roleTitle} jobs →',
    description: 'Link to search results filtered by this role, where {roleTitle} is the job title',
  },
  overallPanelTitle: {
    id: 'salaryRolePage.overallPanel.title',
    defaultMessage: '{roleTitle} — Finland Average',
    description: 'Title of the national salary stats panel, where {roleTitle} is the job title',
  },
  statMedian: {
    id: 'salaryRolePage.stat.median',
    defaultMessage: 'Median',
    description: 'Label for the median salary stat',
  },
  statP25: {
    id: 'salaryRolePage.stat.p25',
    defaultMessage: 'P25',
    description: 'Label for the 25th percentile salary stat',
  },
  statP75: {
    id: 'salaryRolePage.stat.p75',
    defaultMessage: 'P75',
    description: 'Label for the 75th percentile salary stat',
  },
  statSampleSize: {
    id: 'salaryRolePage.stat.sampleSize',
    defaultMessage: 'Sample size',
    description: 'Label for the number of job listings a stat is based on',
  },
  statAverage: {
    id: 'salaryRolePage.stat.average',
    defaultMessage: 'Average',
    description: 'Label for the mean salary stat, shown in the range-bar detail popover',
  },
  statP90: {
    id: 'salaryRolePage.stat.p90',
    defaultMessage: 'P90',
    description: 'Label for the 90th percentile salary stat, shown in the range-bar detail popover',
  },
  statRange: {
    id: 'salaryRolePage.stat.range',
    defaultMessage: 'Full range',
    description: 'Label for the absolute min-max salary range, shown in the range-bar detail popover',
  },
  statMiddle50: {
    id: 'salaryRolePage.stat.middle50',
    defaultMessage: 'Middle 50%',
    description: 'Label for the P25–P75 band, shown in the range-bar detail popover',
  },
  rangeBarHint: {
    id: 'salaryRolePage.rangeBar.hint',
    defaultMessage: 'Tap or hover for details.',
    description: 'Hint that the salary range bar opens a popover with more stats',
  },
  rangeBarPopoverListings: {
    id: 'salaryRolePage.rangeBar.popoverListings',
    defaultMessage: '{count, plural, one {Based on # job listing} other {Based on # job listings}}',
    description: 'Footnote in the range-bar detail popover stating the sample size',
  },
  overallPanelFootnote: {
    id: 'salaryRolePage.overallPanel.footnote',
    defaultMessage:
      'Monthly EUR · P25–P75 band with median marker · based on {count} job listings',
    description: 'Footnote under the national salary panel, where {count} is the sample size',
  },
  cityTableTitle: {
    id: 'salaryRolePage.cityTable.title',
    defaultMessage: '{roleTitle} Salary by City',
    description: 'Title of the per-city salary breakdown table, where {roleTitle} is the job title',
  },
  cityTableFootnote: {
    id: 'salaryRolePage.cityTable.footnote',
    defaultMessage: 'Bar shows P25–P75 band. Vertical line = median. All values in EUR/month.',
    description: 'Footnote under the per-city salary table',
  },
  ctaBanner: {
    id: 'salaryRolePage.ctaBanner',
    defaultMessage: 'Looking for {roleTitle} roles? {link}',
    description: 'Call-to-action banner linking to open positions, where {roleTitle} is the job title and {link} is the link text',
  },
  ctaBannerLink: {
    id: 'salaryRolePage.ctaBanner.link',
    defaultMessage: 'Browse open positions on JobCrawls →',
    description: 'Link text inside the call-to-action banner',
  },
  disclaimer: {
    id: 'salaryRolePage.disclaimer',
    defaultMessage:
      'Salary data is derived from published job listings and may not represent total compensation. Figures are in EUR/month and may include employer estimates where stated salary ranges are not publicly disclosed.',
    description: 'Legal-style disclaimer at the bottom of a role salary page',
  },
  methodologyLink: {
    id: 'salaryRolePage.methodologyLink',
    defaultMessage: 'How we calculate these numbers',
    description: 'Link from a role salary page to the salary methodology page',
  },
  remotePanelTitle: {
    id: 'salaryRolePage.remotePanel.title',
    defaultMessage: '{roleTitle} — Remote Work in Finland',
    description: 'Title of the nationwide remote-work salary panel, where {roleTitle} is the job title',
  },
  examplePostingsTitle: {
    id: 'salaryRolePage.examplePostings.title',
    defaultMessage: 'Based on postings like these',
    description: 'Heading for the list of real job postings a salary stat was computed from',
  },
  examplePostingInactive: {
    id: 'salaryRolePage.examplePostings.inactiveBadge',
    defaultMessage: 'No longer active',
    description: 'Small badge on an example posting card whose listing has expired',
  },
  trendUp: {
    id: 'salaryRolePage.trend.up',
    defaultMessage: '▲ {percent}% vs last month',
    description: 'Badge showing the median salary increased since last month, where {percent} is the change',
  },
  trendDown: {
    id: 'salaryRolePage.trend.down',
    defaultMessage: '▼ {percent}% vs last month',
    description: 'Badge showing the median salary decreased since last month, where {percent} is the absolute change',
  },
  trendFlat: {
    id: 'salaryRolePage.trend.flat',
    defaultMessage: 'No change vs last month',
    description: 'Badge shown when the median salary is unchanged from last month',
  },
  hiringCompaniesTitle: {
    id: 'salaryRolePage.hiringCompanies.title',
    defaultMessage: 'Companies hiring {roleTitle}s',
    description: 'Heading above the list of companies known to hire for this role, where {roleTitle} is the job title',
  },
  hiringCompaniesSub: {
    id: 'salaryRolePage.hiringCompanies.sub',
    defaultMessage: 'From our employer rankings — some may be worth reaching out to directly even without an open listing.',
    description: 'Subtitle under the companies-hiring-for-this-role section on a salary role page',
  },
  hiringCompaniesEmpty: {
    id: 'salaryRolePage.hiringCompanies.empty',
    defaultMessage: 'Browse all ranked employers to find one hiring for this kind of role.',
    description: 'Shown when no specific company match was found for this role on the best-companies data',
  },
  hiringCompaniesBrowseAll: {
    id: 'salaryRolePage.hiringCompanies.browseAll',
    defaultMessage: 'Browse best employers in Finland →',
    description: 'Link to the best-companies hub from the salary role page',
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const H2_CLASS = 'font-display text-base font-bold text-jc-ink sm:text-lg';

function formatEur(value?: number): string {
  if (value == null) return '—';
  return `€${Math.round(value).toLocaleString('en-FI')}`;
}

function medianMid(stats: SalaryStatistics): number | null {
  if (!stats.median) return null;
  return (stats.median.min + stats.median.max) / 2;
}

// ── Salary range bar ──────────────────────────────────────────────────────────

interface RangeBarProps {
  stats: SalaryStatistics;
  globalMax: number;
  /** Popover title — e.g. a city name, or the role/panel name for the national and remote bars. */
  label?: React.ReactNode;
}

function RangeBarStatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#5A4A3A] dark:text-[#B8A48C]">{label}</span>
      <span className="font-semibold text-jc-ink tabular-nums">{value}</span>
    </div>
  );
}

function RangeBar({ stats, globalMax, label, size = 'md' }: RangeBarProps & { size?: 'sm' | 'md' }) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const min = stats.min ?? 0;
  const max = stats.max ?? 0;
  const p25 = stats.percentile25?.min ?? min;
  const p75 = stats.percentile75?.max ?? max;
  const median = medianMid(stats) ?? (p25 + p75) / 2;
  const average = stats.average ? (stats.average.min + stats.average.max) / 2 : null;
  const p90 = stats.percentile90?.max ?? null;
  const sampleSize = stats.sampleSize ?? stats.count ?? 0;

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Element | null>(null);
  const prefersFineHover = usePrefersFineHover();
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHover = useCallback(() => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    clearHover();
    hoverTimer.current = setTimeout(() => {
      setOpen(false);
      hoverTimer.current = null;
    }, 220);
  }, [clearHover]);
  useEffect(() => () => clearHover(), [clearHover]);

  const openPopover = (el: Element) => {
    clearHover();
    setAnchor(el);
    setOpen(true);
  };
  const closePopover = () => {
    clearHover();
    setOpen(false);
  };
  const togglePopover = (el: Element) => {
    setOpen((prev) => {
      if (prev) return false;
      setAnchor(el);
      return true;
    });
  };

  if (globalMax === 0) return null;

  const toPercent = (v: number) => `${Math.min(100, (v / globalMax) * 100).toFixed(1)}%`;
  const height = size === 'sm' ? 'h-2' : 'h-3';
  const markerHeight = size === 'sm' ? 'h-3' : 'h-4';

  return (
    <>
      <div
        className={`relative ${height} cursor-pointer touch-manipulation rounded-full bg-jc-elevated outline-none ${JD_MORE_INFO_CHART_HIT}`}
        aria-hidden="true"
        onClick={(e) => togglePopover(e.currentTarget)}
        onMouseEnter={(e) => prefersFineHover && openPopover(e.currentTarget)}
        onMouseLeave={() => prefersFineHover && scheduleClose()}
      >
        {/* P25–P75 band */}
        <div
          className="absolute h-full rounded-full bg-gradient-to-r from-jc-accent-soft to-jc-accent"
          style={{ left: toPercent(p25), width: `calc(${toPercent(p75)} - ${toPercent(p25)})` }}
        />
        {/* Median marker */}
        <div
          className={`absolute -top-0.5 w-[3px] rounded-full bg-jc-ink ${markerHeight}`}
          style={{ left: toPercent(median) }}
        />
      </div>
      <p className="sr-only">
        {formatMessage(messages.statMedian)}: {formatEur(median)}/mo.{' '}
        {average != null ? `${formatMessage(messages.statAverage)}: ${formatEur(average)}/mo. ` : ''}
        {formatMessage(messages.statMiddle50)}: {formatEur(p25)} – {formatEur(p75)}.{' '}
        {p90 != null ? `${formatMessage(messages.statP90)}: ${formatEur(p90)}/mo. ` : ''}
        {formatMessage(messages.statRange)}: {formatEur(min)} – {formatEur(max)}.{' '}
        <Msg {...messages.rangeBarPopoverListings} values={{ count: sampleSize }} />
      </p>
      {open ? (
        <ChartPopoverPortal
          open
          anchorEl={anchor}
          onClose={closePopover}
          showBackdrop={!prefersFineHover}
          title={label ?? formatMessage(messages.statMedian)}
          onPopoverMouseEnter={prefersFineHover ? clearHover : undefined}
          onPopoverMouseLeave={prefersFineHover ? scheduleClose : undefined}
        >
          <div className="space-y-1.5">
            <RangeBarStatRow label={formatMessage(messages.statMedian)} value={formatEur(median) + '/mo'} />
            {average != null ? (
              <RangeBarStatRow label={formatMessage(messages.statAverage)} value={formatEur(average) + '/mo'} />
            ) : null}
            <RangeBarStatRow
              label={formatMessage(messages.statMiddle50)}
              value={`${formatEur(p25)} – ${formatEur(p75)}`}
            />
            {p90 != null ? (
              <RangeBarStatRow label={formatMessage(messages.statP90)} value={formatEur(p90) + '/mo'} />
            ) : null}
            <RangeBarStatRow label={formatMessage(messages.statRange)} value={`${formatEur(min)} – ${formatEur(max)}`} />
            <p className="border-t border-jc-border/80 pt-1.5 text-xs text-jc-muted">
              <Msg {...messages.rangeBarPopoverListings} values={{ count: sampleSize }} />
            </p>
          </div>
        </ChartPopoverPortal>
      ) : null}
    </>
  );
}

// ── Trend indicator (shared component) ────────────────────────────────────────

function SalaryTrendIndicator({ trend }: { trend: SnapshotTrend | null | undefined }) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  if (!trend || trend.percentChange == null) return null;
  const { percentChange } = trend;
  const direction: TrendDirection = percentChange === 0 ? 'flat' : percentChange > 0 ? 'up' : 'down';
  const label =
    percentChange === 0
      ? formatMessage(messages.trendFlat)
      : formatMessage(percentChange > 0 ? messages.trendUp : messages.trendDown, { percent: Math.abs(percentChange) });
  return <TrendIndicator direction={direction}>{label}</TrendIndicator>;
}

// ── Overall stats panel ───────────────────────────────────────────────────────

/** Hero: the page's signature moment — a large Space Grotesk median figure + range bar,
 * replacing the old "national panel + separate h1" split so the number leads the page. */
function SalaryHero({
  stats,
  roleTitle,
  headingTitle,
  trend,
  srpUrl,
  otherTitles,
}: {
  stats: SalaryStatistics;
  roleTitle: string;
  headingTitle: string;
  trend?: SnapshotTrend | null;
  srpUrl: string;
  otherTitles: string[];
}) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const median = medianMid(stats);
  const p25 = stats.percentile25?.min;
  const p75 = stats.percentile75?.max;
  const sampleSize = stats.sampleSize ?? stats.count ?? 0;
  const globalMax = stats.max ? stats.max * 1.1 : 10000;

  return (
    <JcCard className="mb-5 p-5 sm:p-7">
      {sampleSize > 0 ? (
        <span className="inline-flex items-center rounded-full bg-jc-gold-bg px-3 py-1 text-xs font-bold text-jc-gold">
          <Msg
            id="salaryRolePage.hero.sampleEyebrow"
            defaultMessage="Based on {count} postings"
            description="Eyebrow chip above the salary hero heading, stating the sample size"
            values={{ count: sampleSize }}
          />
        </span>
      ) : null}
      <h1 className="font-display mt-3 text-2xl font-bold text-jc-ink sm:text-3xl">
        <Msg {...messages.heading} values={{ roleTitle: headingTitle }} />
      </h1>
      {otherTitles.length > 0 ? (
        <p className="mt-1 text-xs text-jc-muted">
          <Msg {...messages.alsoKnownAs} values={{ titles: otherTitles.join(', ') }} />
        </p>
      ) : null}
      <div className="mt-2">
        {/* roleTitle here is the locale-appropriate display title (see +Page.tsx's `displayTitle`
            comment) captured verbatim from a real posting's normalisedTitle_{locale} field by
            generateSalaryStats.ts — the same string space the jobs index facets on, so this
            follows the same role a job-detail page's RoleFollowButton would. */}
        <RoleFollowButton roleTitle={roleTitle} surface="salary-role-page" />
      </div>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-4xl font-bold tabular-nums text-jc-ink sm:text-5xl">
              {median != null ? formatEur(median) : '—'}
            </span>
            <span className="text-sm font-semibold text-jc-muted">
              <Msg
                id="salaryRolePage.hero.perMonthNational"
                defaultMessage="/ month, national median"
                description="Unit label after the large median salary figure in the hero"
              />
            </span>
          </div>
          {trend ? <div className="mt-2"><SalaryTrendIndicator trend={trend} /></div> : null}
          {median != null ? (
            <div className="mt-3">
              <TaxIcebergLink grossMonthly={median} size="md" />
            </div>
          ) : null}
        </div>
        <div className="flex gap-6">
          <Stat label={formatMessage(messages.statP25)} value={p25 != null ? formatEur(p25) + '/mo' : '—'} />
          <Stat label={formatMessage(messages.statP75)} value={p75 != null ? formatEur(p75) + '/mo' : '—'} />
        </div>
      </div>

      <div className="mt-5">
        <RangeBar stats={stats} globalMax={globalMax} label={formatMessage(messages.overallPanelTitle, { roleTitle })} />
      </div>
      <p className="mt-3 text-xs text-jc-muted">
        <Msg {...messages.overallPanelFootnote} values={{ count: sampleSize }} />{' '}
        <Msg {...messages.rangeBarHint} />
      </p>

      <p className="mt-4 text-sm text-jc-muted">
        <Msg {...messages.intro} />{' '}
        <Link href={srpUrl} className="text-jc-accent hover:text-jc-cta-hover hover:underline font-medium">
          <Msg {...messages.seeOpenJobs} values={{ roleTitle }} />
        </Link>
      </p>
    </JcCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] text-jc-muted">{label}</p>
      <p className="font-display text-base font-bold tabular-nums text-jc-ink">{value}</p>
    </div>
  );
}

// ── Remote work panel ────────────────────────────────────────────────────────

function RemoteStatsPanel({ stats, roleTitle }: { stats: SalaryStatistics; roleTitle: string }) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const median = medianMid(stats);
  const p25 = stats.percentile25?.min;
  const p75 = stats.percentile75?.max;
  const globalMax = stats.max ? stats.max * 1.1 : 10000;

  return (
    <JcCard className="mb-5 p-5 sm:p-6">
      <h2 className={H2_CLASS}>
        <Msg {...messages.remotePanelTitle} values={{ roleTitle }} />
      </h2>
      <div className="mt-4 flex flex-wrap gap-x-7 gap-y-3">
        <Stat label={formatMessage(messages.statMedian)} value={median != null ? formatEur(median) + '/mo' : '—'} />
        <Stat label={formatMessage(messages.statP25)} value={p25 != null ? formatEur(p25) + '/mo' : '—'} />
        <Stat label={formatMessage(messages.statP75)} value={p75 != null ? formatEur(p75) + '/mo' : '—'} />
        <Stat label={formatMessage(messages.statSampleSize)} value={String(stats.sampleSize ?? stats.count ?? '—')} />
      </div>
      <div className="mt-4">
        <RangeBar stats={stats} globalMax={globalMax} label={formatMessage(messages.remotePanelTitle, { roleTitle })} />
      </div>
    </JcCard>
  );
}

// ── Example postings ─────────────────────────────────────────────────────────

function ExamplePostings({ postings }: { postings: ExamplePosting[] }) {
  if (postings.length === 0) return null;

  return (
    <JcCard className="mb-5 p-5 sm:p-6">
      <h2 className={`${H2_CLASS} mb-3`}>
        <Msg {...messages.examplePostingsTitle} />
      </h2>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {postings.map((posting) => (
          <Link
            key={posting.href}
            href={posting.href}
            className="group flex items-center gap-3 rounded-2xl border border-jc-border bg-jc-page/50 p-3 transition-colors hover:border-jc-accent-soft hover:bg-jc-elevated/40"
          >
            <CompanyLogo
              companyName={posting.companyId || posting.company}
              size={40}
              alt={posting.company}
              variant="rounded"
              isTyomarkkinatori={posting.isTyomarkkinatori}
              className={posting.isActive ? undefined : 'opacity-60'}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-jc-ink transition-colors group-hover:text-jc-accent">
                {posting.title}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-jc-muted">
                {posting.company ? <span className="truncate">{posting.company}</span> : null}
                {posting.company && posting.location ? <span aria-hidden>·</span> : null}
                {posting.location ? (
                  <span className="inline-flex min-w-0 items-center gap-0.5">
                    <MapPin className="h-3 w-3 shrink-0" weight="fill" />
                    <span className="truncate">{posting.location}</span>
                  </span>
                ) : null}
              </p>
            </div>
            {!posting.isActive ? (
              <span className="shrink-0 rounded-full bg-jc-elevated px-2 py-0.5 text-[10px] font-semibold text-jc-muted">
                <Msg {...messages.examplePostingInactive} />
              </span>
            ) : null}
          </Link>
        ))}
      </div>
    </JcCard>
  );
}

// ── City table ────────────────────────────────────────────────────────────────

function CityTable({ cityStats, roleTitle }: { cityStats: Array<{ city: string; stats: SalaryStatistics }>; roleTitle: string }) {
  if (cityStats.length === 0) return null;

  const sortedByMedian = [...cityStats].sort((a, b) => {
    const aM = medianMid(a.stats) ?? 0;
    const bM = medianMid(b.stats) ?? 0;
    return bM - aM;
  });

  const globalMax = Math.max(...cityStats.map(cs => cs.stats.max ?? 0)) * 1.1 || 10000;

  return (
    <JcCard className="mb-5 p-5 sm:p-6">
      <h2 className={`${H2_CLASS} mb-4`}>
        <Msg {...messages.cityTableTitle} values={{ roleTitle }} />
      </h2>
      <div className="space-y-4">
        {sortedByMedian.map(({ city, stats }) => {
          const median = medianMid(stats);
          const cityName = city.split(',')[0];
          return (
            <div key={city}>
              <div className="flex items-baseline justify-between mb-1.5 gap-2">
                <span className="text-sm font-semibold text-jc-ink">{cityName}</span>
                <div className="text-right shrink-0">
                  <span className="font-display text-sm font-bold text-jc-ink tabular-nums">
                    {median != null ? `${formatEur(median)}/mo` : '—'}
                  </span>
                  <span className="text-xs text-jc-muted ml-2 tabular-nums">
                    n={stats.sampleSize ?? stats.count ?? 0}
                  </span>
                </div>
              </div>
              <RangeBar stats={stats} globalMax={globalMax} size="sm" label={`${roleTitle} · ${cityName}`} />
            </div>
          );
        })}
      </div>
      <p className="text-xs text-jc-muted mt-4">
        <Msg {...messages.cityTableFootnote} />{' '}
        <Msg {...messages.rangeBarHint} />
      </p>
    </JcCard>
  );
}

// ── Companies hiring for this role ──────────────────────────────────────────────

/**
 * Bridges salary data back to company discovery: matched server-side in +data.server.ts
 * (topRoles lookup against the same ranked-companies source the best-companies hub uses) so
 * this internal link is in the initial HTML, not only reachable after a client-side fetch —
 * a role page shouldn't dead-end on pay data alone when real employers are one query away.
 */
function CompaniesHiringForRole({ roleTitle, locale, companies }: { roleTitle: string; locale: string; companies: AlgoliaCompanyRecord[] }) {
  return (
    <JcCard className="mb-5 p-5 sm:p-6">
      <h2 className={H2_CLASS}>
        <Msg {...messages.hiringCompaniesTitle} values={{ roleTitle }} />
      </h2>
      <p className="mt-1 mb-3 text-xs text-jc-muted">
        <Msg {...messages.hiringCompaniesSub} />
      </p>

      {companies.length > 0 ? (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {companies.map((company) => {
            const networkWorthy = isNetworkWorthy(company);
            return (
              <Link
                key={company.objectID}
                href={companyProfileUrlPath(locale as 'en' | 'fi' | 'sv', company.objectID, company.profileSlug)}
                locale={locale}
                className="group flex items-center gap-3 rounded-2xl border border-jc-border bg-jc-page/50 p-3 transition-colors hover:border-jc-accent-soft hover:bg-jc-elevated/40"
              >
                <CompanyLogo companyName={company.objectID} size={40} alt={company.companyName ?? ''} variant="rounded" isTyomarkkinatori={false} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-jc-ink transition-colors group-hover:text-jc-accent">
                    {company.companyName}
                  </p>
                  {networkWorthy ? (
                    <p className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-jc-gold-bg px-2 py-0.5 text-[10px] font-bold text-jc-gold">
                      <Handshake className="h-2.5 w-2.5" weight="fill" />
                      <Msg id="bestCompanies.row.networkWorthy" defaultMessage="Worth networking with" />
                    </p>
                  ) : company.registryCity ? (
                    <p className="truncate text-xs text-jc-muted">{company.registryCity}</p>
                  ) : null}
                </div>
                {typeof company.employerScore === 'number' ? (
                  <EmployerScoreGauge
                    score={company.employerScore}
                    displayText={mapEmployerScoreToTier(company.employerScore)}
                    size={36}
                    strokeWidth={4}
                    color="var(--jc-accent)"
                    trackColor="var(--jc-elevated)"
                    className="flex-none"
                  />
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-jc-muted">
          <Msg {...messages.hiringCompaniesEmpty} />{' '}
          <Link href="/best-companies" locale={locale} className="text-jc-accent hover:text-jc-cta-hover font-medium hover:underline">
            <Msg {...messages.hiringCompaniesBrowseAll} />
          </Link>
        </p>
      )}
    </JcCard>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SalaryRolePage() {
  // useData()/usePageContext() reliably return undefined on the client in this app (see
  // parseSalaryRoleInitialScript.ts) — fall back to the SSR-embedded script payload, same
  // pattern as every other content page here (job detail, SRP, best-companies).
  const dataFromHook = useData<Data>();
  const data = dataFromHook ?? getSalaryRoleDataFromScript();
  const pageContext = usePageContext();
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  if (!data) {
    return null;
  }
  const { roleTitle, roleTitleDisplay, overallStats, cityStats, remoteStats, examplePostings, hiringCompanies, snapshotTrend, otherTitles } = data;
  const locale = (data.locale ?? pageContext?.locale ?? 'en') as string;
  const localePrefix = locale === 'en' ? '' : `/${locale}`;
  // srpUrl filters search results by normalisedTitle, which is indexed in English regardless of
  // page locale — must stay on the English roleTitle, never the localized display name.
  const srpUrl = `${localePrefix}/?normalisedTitle=${encodeURIComponent(roleTitle)}`;
  const breadcrumbHref = salaryHubUrlPath(locale as 'en' | 'fi' | 'sv');
  // Everything below is visible page text, not a data key — uses roleTitleDisplay (the fi/sv
  // name when a real posting supplied one, English otherwise) so the page actually contains the
  // words a Finnish/Swedish searcher typed, instead of an English role name wrapped in fi/sv
  // sentences.
  const displayTitle = roleTitleDisplay ?? roleTitle;
  // The H1 ("{roleTitle} palkka Suomessa") needs the Finnish genitive ("Sairaanhoitajan"), not
  // the nominative ("Sairaanhoitaja") — see finnish-role-genitive.ts. Only the heading needs this;
  // seeOpenJobs/overallPanelTitle use displayTitle as-is (hyphen-compound / dash constructions
  // that don't take genitive).
  const headingTitle = locale === 'fi' ? getFinnishRoleGenitive(displayTitle) : displayTitle;
  const hubLocale = (locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en') as 'en' | 'fi' | 'sv';

  return (
    <div className="min-h-screen bg-jc-page">
      <script
        id={SALARY_ROLE_PAGE_SCRIPT_ID}
        type="application/json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
      />
      <div className="max-w-4xl mx-auto px-3 pt-1.5 pb-12 sm:px-4 sm:pt-4 sm:pb-16">
        <BreadcrumbNavigation
          items={[
            { label: formatMessage(messages.breadcrumbSalaryData), href: breadcrumbHref },
            { label: displayTitle },
          ]}
        />

        {overallStats ? (
          <SalaryHero
            stats={overallStats}
            roleTitle={displayTitle}
            headingTitle={headingTitle}
            trend={snapshotTrend}
            srpUrl={srpUrl}
            otherTitles={otherTitles}
          />
        ) : (
          <div className="mt-4 mb-5">
            <h1 className="font-display text-2xl font-bold text-jc-ink sm:text-3xl">
              <Msg {...messages.heading} values={{ roleTitle: headingTitle }} />
            </h1>
            {otherTitles.length > 0 ? (
              <p className="mt-1 text-xs text-jc-muted">
                <Msg {...messages.alsoKnownAs} values={{ titles: otherTitles.join(', ') }} />
              </p>
            ) : null}
            <p className="mt-2 text-sm text-jc-muted">
              <Msg {...messages.intro} />{' '}
              <Link href={srpUrl} className="text-jc-accent hover:text-jc-cta-hover hover:underline font-medium">
                <Msg {...messages.seeOpenJobs} values={{ roleTitle: displayTitle }} />
              </Link>
            </p>
          </div>
        )}
        <ExamplePostings postings={examplePostings} />
        {remoteStats && <RemoteStatsPanel stats={remoteStats} roleTitle={displayTitle} />}
        <CityTable cityStats={cityStats} roleTitle={displayTitle} />
        <CompaniesHiringForRole roleTitle={displayTitle} locale={locale} companies={hiringCompanies ?? []} />

        <div className="mt-2 mb-5 p-4 rounded-2xl bg-gradient-to-br from-jc-accent-muted/70 to-jc-elevated dark:from-jc-accent-muted/20 dark:to-jc-elevated border-2 border-jc-accent/25 flex items-start gap-3">
          <Buildings className="h-5 w-5 text-jc-accent shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-jc-ink-secondary">
            <Msg
              {...messages.ctaBanner}
              values={{
                roleTitle: displayTitle,
                link: (
                  <Link href={srpUrl} className="text-jc-accent hover:text-jc-cta-hover font-semibold hover:underline">
                    {formatMessage(messages.ctaBannerLink)}
                  </Link>
                ),
              }}
            />
          </p>
        </div>

        <HubCrossLinks
          variant="chips"
          locale={hubLocale}
          heading={hubLinksHeading(hubLocale)}
          className="mb-5"
          links={[
            ...((): HubCrossLink[] => {
              const hub = findRoleByDisplayName(roleTitle) ?? findRoleByDisplayName(displayTitle) ?? findRoleForAnyLocaleTitle(roleTitle);
              if (!hub) return [];
              const name = hub.displayNames[hubLocale];
              const label = hubLocale === 'fi' ? `${finnishRolePhrase(name, 'työpaikat')} Suomessa` : hubLocale === 'sv' ? `Lediga jobb som ${name}` : `${name} jobs in Finland`;
              return [{ kind: 'role', href: buildRoleHubPath(hub.slug, hubLocale), label }];
            })(),
            ...buildHubIndexLinks(hubLocale, ['salary', 'roles', 'employers']),
          ]}
        />

        <p className="text-xs text-jc-muted leading-relaxed">
          <Msg {...messages.disclaimer} />{' '}
          <Link href="/salary-methodology" locale={locale} className="font-medium text-jc-cta hover:text-jc-cta-hover underline-offset-2 hover:underline">
            <Msg {...messages.methodologyLink} />
          </Link>
        </p>
      </div>
    </div>
  );
}
